import { computed, onMounted, ref, watch, type Ref } from 'vue'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import type { GridCol, GridItem } from '~~/types/orgGrid'
import { useDirections } from '~/composables/orga/useDirections'
import { useDomaines } from '~/composables/orga/useDomaine'
import { useAgences } from '~/composables/orga/useAgence'
import { useEquipes } from '~/composables/equipe/useEquipe'
import {
  classifyUnknownToScreenModel,
  APP_ERROR_ILLUSTRATION_NOT_FOUND_OR_API,
  type AppErrorScreenModel,
} from '~/composables/ui/useAppError'
import { domainEntriesInApiOrder } from '~~/utils/organization/siblingOrder'

export type DirectionPageState = 'loading' | 'ready' | 'error' | 'notFound'

export const DIRECTION_PAGE_INIT_COL_COUNT = 3
export const DIRECTION_PAGE_INIT_ITEMS_PER_COL = 5

type CodirState = {
  label: string | undefined
  to?: string
  libLong?: string | null
  desc?: string | null
}

type DomainEntry = {
  domainId: string
  domLib: string
}

type DirectionPageLoadDeps = {
  route: RouteLocationNormalizedLoaded
  fetchDirectionById: ReturnType<typeof useDirections>['fetchDirectionById']
  fetchDomaineById: ReturnType<typeof useDomaines>['fetchDomaineById']
  fetchAgenceById: ReturnType<typeof useAgences>['fetchAgenceById']
  hasEquipeById: ReturnType<typeof useEquipes>['hasEquipeById']
  pageState: Ref<DirectionPageState>
  errorScreenModel: Ref<AppErrorScreenModel | null>
  isDirectionOrganisationEmpty: Ref<boolean>
  title: Ref<string | null>
  grid: Ref<GridCol[]>
  directionLib: Ref<string>
  codir: Ref<CodirState>
}

export function useDirectionPageLoad() {
  const route = useRoute()
  const { fetchDirectionById } = useDirections()
  const { fetchDomaineById } = useDomaines()
  const { fetchAgenceById } = useAgences()
  const { hasEquipeById } = useEquipes()
  const pageState = ref<DirectionPageState>('loading')
  const errorScreenModel = ref<AppErrorScreenModel | null>(null)
  const isDirectionOrganisationEmpty = ref(false)
  const title = ref<string | null>(null)
  const grid = ref<GridCol[]>([])
  const directionLib = ref<string>('')
  const codir = ref<CodirState>({ label: undefined, to: undefined, libLong: null, desc: null })
  const isReadyForHighlight = computed(() => pageState.value === 'ready')
  const deps: DirectionPageLoadDeps = {
    route,
    fetchDirectionById,
    fetchDomaineById,
    fetchAgenceById,
    hasEquipeById,
    pageState,
    errorScreenModel,
    isDirectionOrganisationEmpty,
    title,
    grid,
    directionLib,
    codir,
  }

  onMounted(() => {
    mountDirectionPage(deps)
  })
  watch(
    () => route.params.direction,
    () => {
      onDirectionParamChange(deps)
    },
  )

  return {
    pageState,
    errorScreenModel,
    isDirectionOrganisationEmpty,
    title,
    grid,
    directionLib,
    codir,
    isReadyForHighlight,
    loadData: (dirLib: string) => loadDirectionPage(deps, dirLib),
    refetch: () => refetchDirectionPage(deps),
  }
}

function lastIdSegment(compoundId: string): string | undefined {
  const parts = compoundId.split('~')
  return parts[parts.length - 1]
}

function isUsableLib(value: string | null | undefined): value is string {
  return value != null && value !== '' && String(value).toLowerCase() !== 'null'
}

function equipeRouteFromId(id: string): string {
  return `/${id.split('~').map(encodeURIComponent).join('/')}`
}

function parseDomainEntries(domainsId: string[], dirLib: string): DomainEntry[] {
  return domainEntriesInApiOrder(domainsId, dirLib)
}

function emptyColumn(domLib: string): GridCol {
  return {
    header: { label: domLib, to: undefined, libLong: null, desc: null },
    items: [],
  }
}

function agenceItemStub(ageLib: string): GridItem {
  return {
    label: ageLib,
    to: undefined,
    libLong: null,
    desc: null,
  }
}

function agenceDetailRoute(agence: {
  dirLib: string
  domLib: string
  ageLib: string
}): string {
  return [
    encodeURIComponent(agence.dirLib),
    encodeURIComponent(agence.domLib),
    encodeURIComponent(agence.ageLib),
    encodeURIComponent(agence.ageLib),
  ].join('/')
}

function resolveDirectionParam(route: RouteLocationNormalizedLoaded): string | null {
  const dirParam = route.params.direction
  const dirLib = typeof dirParam === 'string' ? dirParam : dirParam?.[0]
  if (!dirLib) {
    return null
  }
  return decodeURIComponent(dirLib)
}

function resetDirectionLoading(deps: DirectionPageLoadDeps): void {
  deps.pageState.value = 'loading'
  deps.errorScreenModel.value = null
  deps.isDirectionOrganisationEmpty.value = false
  deps.title.value = null
  deps.grid.value = []
}

function applyDirectionNotFound(deps: DirectionPageLoadDeps, dirLib: string): void {
  deps.pageState.value = 'notFound'
  deps.errorScreenModel.value = {
    visualKind: 'not_found',
    title: 'Direction introuvable',
    description: `Aucune direction ne correspond à « ${dirLib} ».`,
    illustrationUrl: APP_ERROR_ILLUSTRATION_NOT_FOUND_OR_API,
    blocking: true,
    showBackButton: true,
    showHomeButton: true,
    showSessionReset: false,
  }
}

function applyDirectionLoadError(deps: DirectionPageLoadDeps, caught: unknown): void {
  console.error('[Direction page] loadData', caught)
  const model = classifyUnknownToScreenModel(caught)
  deps.errorScreenModel.value = model
  deps.pageState.value = model.visualKind === 'not_found' ? 'notFound' : 'error'
  deps.title.value = null
}

function hydrateDirectionCodir(
  deps: DirectionPageLoadDeps,
  dirLib: string,
  direction: { dirLibLong?: string | null; dirDesc?: string | null },
): void {
  deps.codir.value = {
    label: 'CODIR',
    to: undefined,
    libLong: direction.dirLibLong ?? null,
    desc: direction.dirDesc ?? null,
  }
  void deps.hasEquipeById(dirLib).then((equipeId) => {
    if (equipeId === null) return
    deps.codir.value = { ...deps.codir.value, to: equipeRouteFromId(equipeId) }
  })
}

function findDirectionColumn(columns: GridCol[], domLib: string): GridCol | undefined {
  return columns.find((column) => column.header.label === domLib)
}

function enrichAgenceRow(deps: DirectionPageLoadDeps, column: GridCol, agenceId: string): void {
  void deps.fetchAgenceById(agenceId).then((resAg) => {
    if (resAg == null) return
    const agence = resAg.agence
    const item = column.items.find((row) => row.label === agence.ageLib)
    if (!item) return
    item.libLong = agence.ageLibLong ?? null
    item.desc = agence.ageDesc ?? null
    item.to = `/${agenceDetailRoute(agence)}`
  })
}

function sortedAgenceLibs(agencesId: string[]): string[] {
  return agencesId
    .map((agenceId) => lastIdSegment(agenceId))
    .filter(isUsableLib)
    .sort((left, right) => left.localeCompare(right))
}

function agenceIdsOfDomain(agencesId: string[], domLib: string): string[] {
  return agencesId.filter((agenceId) => lastIdSegment(agenceId) !== domLib)
}

function enrichDomainColumn(deps: DirectionPageLoadDeps, entry: DomainEntry): void {
  void deps.fetchDomaineById(entry.domainId).then((resDom) => {
    if (resDom == null) return
    const domain = resDom.domaine
    const column = findDirectionColumn(deps.grid.value, entry.domLib)
    if (!column) return

    column.header.libLong = domain.domLibLong ?? null
    column.header.desc = domain.domDesc ?? null
    void deps.hasEquipeById(domain.dirLib, domain.domLib).then((equipeId) => {
      if (equipeId === null) return
      column.header.to = equipeRouteFromId(equipeId)
    })

    const agencesId = agenceIdsOfDomain(resDom.agencesId ?? [], domain.domLib)
    column.items = sortedAgenceLibs(agencesId).map(agenceItemStub)
    for (const agenceId of agencesId) {
      enrichAgenceRow(deps, column, agenceId)
    }
  })
}

function applyDirectionDomainsGrid(
  deps: DirectionPageLoadDeps,
  dirLib: string,
  domainsId: string[],
): void {
  const domainLibsSorted = parseDomainEntries(domainsId, dirLib)
  if (domainLibsSorted.length === 0) {
    deps.grid.value = []
    deps.isDirectionOrganisationEmpty.value = true
    deps.pageState.value = 'ready'
    return
  }

  deps.isDirectionOrganisationEmpty.value = false
  deps.grid.value = domainLibsSorted.map(({ domLib }) => emptyColumn(domLib))
  for (const entry of domainLibsSorted) {
    enrichDomainColumn(deps, entry)
  }
  deps.pageState.value = 'ready'
}

async function loadDirectionPage(deps: DirectionPageLoadDeps, dirLib: string): Promise<void> {
  resetDirectionLoading(deps)

  try {
    const resDir = await deps.fetchDirectionById(dirLib)
    if (resDir == null) {
      applyDirectionNotFound(deps, dirLib)
      return
    }

    const direction = resDir.direction
    deps.directionLib.value = direction?.dirLib ?? dirLib
    deps.title.value = direction.dirLibLong ?? null
    hydrateDirectionCodir(deps, dirLib, direction)
    applyDirectionDomainsGrid(deps, dirLib, resDir.domainsId ?? [])
  } catch (caught: unknown) {
    applyDirectionLoadError(deps, caught)
  }
}

function refetchDirectionPage(deps: DirectionPageLoadDeps): Promise<void> {
  if (!deps.directionLib.value) {
    return Promise.resolve()
  }
  return loadDirectionPage(deps, deps.directionLib.value)
}

function mountDirectionPage(deps: DirectionPageLoadDeps): void {
  const decoded = resolveDirectionParam(deps.route)
  if (decoded) {
    void loadDirectionPage(deps, decoded)
    return
  }
  void navigateTo('/')
}

function onDirectionParamChange(deps: DirectionPageLoadDeps): void {
  const decoded = resolveDirectionParam(deps.route)
  if (decoded) {
    void loadDirectionPage(deps, decoded)
  }
}
